import dayjs from "dayjs";
import { CHART_INK } from "./channel";

/**
 * Sundays wear the platform's red, the same convention the sale reports use for a
 * non-working day. It is a status colour, never one of the channel hues — nothing on these
 * charts is coloured red as data, so a red label can only mean "this is a Sunday".
 */
export const SUNDAY_INK = "#C62828";

/**
 * Is this bucket key a Sunday?
 *
 * Month buckets ("YYYY-MM") answer false rather than guessing from the 1st of the month —
 * a month has no weekday, and colouring it by one would be an invented fact.
 */
export const isSundayBucket = (bucket: string): boolean => {
  if (bucket.split("-").length !== 3) return false;
  // Single-argument parse on purpose: `YYYY-MM-DD` is ISO and needs no format string, and
  // the two-argument form would silently ignore its format without the customParseFormat
  // plugin, which this app does not load.
  const parsed = dayjs(bucket);
  return parsed.isValid() && parsed.day() === 0;
};

interface BucketTickProps {
  x?: number;
  y?: number;
  payload?: { value?: string | number; index?: number };
  /** Bucket keys in chart order — `payload.index` indexes into this. */
  buckets: string[];
}

/**
 * X-axis tick that reddens Sundays.
 *
 * It takes the bucket KEYS rather than reading the rendered label, because the label is
 * already localised for display ("5 ก.ย.") and parsing a weekday back out of Thai text
 * would be a second, worse source of truth for something the data already knows.
 *
 * Shared by both charts on this screen so a Sunday is never red on one and black on the
 * other.
 */
export default function BucketTick({
  x,
  y,
  payload,
  buckets,
}: Readonly<BucketTickProps>) {
  const index = payload?.index ?? -1;
  const bucket = buckets[index] ?? "";
  const sunday = isSundayBucket(bucket);

  return (
    <text
      x={x}
      y={y}
      dy={12}
      textAnchor="middle"
      fill={sunday ? SUNDAY_INK : CHART_INK}
      fontSize={12}
      fontWeight={sunday ? 600 : 400}
    >
      {payload?.value}
    </text>
  );
}
